import { LitElement, html, css } from "lit";

/**
 * Image card grid at a fixed 2:3 aspect ratio.
 *
 * On pointer/hover-capable devices, hovering a card triggers a CSS 3D flip
 * revealing `cardFields` values on the back. On touch-only devices the flip
 * is suppressed entirely: the image sits on top and field values appear as a
 * caption strip below. A missing or empty `imageField` value renders a solid
 * color fallback block with the first cardField value centered.
 *
 * @element data-card-catalog
 * @prop {Array}  entries    - Array of entry objects; each must include a `url` string.
 * @prop {String} imageField - Entry property key holding the image URL.
 * @prop {Array}  cardFields - Ordered field names to display on the card back / caption.
 *                             First field is treated as the primary label (title).
 */
export class DataCardCatalogElement extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    .card-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(9rem, 1fr));
      gap: var(--space-sm);
    }

    /* ── Card shell ── */
    .card {
      display: block;
      text-decoration: none;
      color: inherit;
    }

    .card__inner {
      position: relative;
      /* Override via --card-aspect-ratio custom property on the host element */
      aspect-ratio: var(--card-aspect-ratio, 2 / 3);
      border: 1px solid var(--color-border);
    }

    /* ── Card faces ── */
    .card__face {
      position: absolute;
      inset: 0;
      overflow: hidden;
      border-radius: 0.25rem;
    }

    /* Back face hidden on mobile — caption carries that info instead */
    .card__face--back {
      display: none;
    }

    /* ── Front face ── */
    .card__face--front img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    .card__fallback {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--space-sm);
      text-align: center;
      background: color-mix(in srgb, var(--color-teal) 15%, var(--color-surface));
      color: var(--color-teal);
      font-size: var(--step--1);
      font-weight: bold;
      line-height: 1.3;
    }

    /* ── Caption (mobile only) ── */
    .card__caption {
      padding: var(--space-xs) 0;
      font-size: var(--step--1);
      line-height: 1.4;
    }

    .card__caption p {
      margin: 0;
    }

    .card__caption p:first-child {
      font-weight: bold;
    }

    .card__caption p + p {
      opacity: 0.7;
    }

    /* ── Flip (pointer + hover capable devices only) ── */
    @media (hover: hover) and (pointer: fine) {
      .card {
        perspective: 800px;
      }

      .card__inner {
        transform-style: preserve-3d;
        transition: transform 0.5s ease;
      }

      .card:hover .card__inner,
      .card:focus-visible .card__inner {
        transform: rotateY(180deg);
      }

      .card__face {
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
      }

      .card__face--back {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--space-sm);
        padding: var(--space-md);
        text-align: center;
        background: var(--color-teal);
        color: var(--color-surface);
        transform: rotateY(180deg);
      }

      .card__face--back span:first-child {
        font-size: var(--step-0);
        font-weight: bold;
        line-height: 1.3;
      }

      .card__face--back span + span {
        font-size: var(--step--1);
        opacity: 0.8;
      }

      /* Caption is redundant on hover-capable devices */
      .card__caption {
        display: none;
      }
    }
  `;

  static properties = {
    entries:    { type: Array },
    imageField: { type: String, attribute: "image-field" },
    cardFields: { type: Array,  attribute: "card-fields" },
  };

  constructor() {
    super();
    this.entries    = [];
    this.imageField = "";
    this.cardFields = [];
  }

  /**
   * @param {Object} entry
   * @returns {import('lit').TemplateResult}
   */
  _renderCard(entry) {
    const imageUrl  = this.imageField ? entry[this.imageField] : null;
    const titleText = String(entry[this.cardFields[0]] ?? "");

    const front = imageUrl
      ? html`<img src="${imageUrl}" alt="${titleText}" loading="lazy" decoding="async" />`
      : html`<div class="card__fallback">${titleText}</div>`;

    return html`
      <a href="${entry.url}" class="card" aria-label="${titleText}">
        <div class="card__inner">
          <div class="card__face card__face--front">${front}</div>
          <div class="card__face card__face--back" aria-hidden="true">
            ${this.cardFields.map((field) => html`<span>${entry[field]}</span>`)}
          </div>
        </div>
        <div class="card__caption" aria-hidden="true">
          ${this.cardFields.map((field) => html`<p>${entry[field]}</p>`)}
        </div>
      </a>
    `;
  }

  render() {
    return html`
      <div class="card-grid" role="list">
        ${this.entries.map((entry) => html`
          <div role="listitem">${this._renderCard(entry)}</div>
        `)}
      </div>
    `;
  }
}

customElements.define("data-card-catalog", DataCardCatalogElement);
